import type { CSSProperties } from 'react'
import { useEffect, useRef } from 'react'
import { OWNER_ACCENT, OWNER_BG, OWNER_BORDER, OWNER_PANEL } from './ownerTheme'

export type DrawerField = { label: string; value: string }
export type DrawerTable = { columns: string[]; rows: string[][] }

type DetailsDrawerProps = {
  open: boolean
  title: string
  subtitle?: string
  fields?: DrawerField[]
  table?: DrawerTable
  onClose: () => void
}

export default function DetailsDrawer({ open, title, subtitle, fields, table, onClose }: DetailsDrawerProps) {
  const closeButtonRef = useRef<HTMLButtonElement | null>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!open) return
    previousFocusRef.current = document.activeElement as HTMLElement | null
    const timer = window.setTimeout(() => closeButtonRef.current?.focus(), 0)
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKey)
    return () => {
      window.removeEventListener('keydown', handleKey)
      window.clearTimeout(timer)
      previousFocusRef.current?.focus?.()
    }
  }, [open, onClose])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50" style={{ '--owner-accent': OWNER_ACCENT } as CSSProperties}>
      <button
        type="button"
        aria-label="Close details drawer"
        className="absolute inset-0 bg-black/60"
        onClick={onClose}
      />
      <aside
        role="dialog"
        aria-modal="true"
        className="absolute right-0 top-0 h-full w-full max-w-md border-l shadow-[0_20px_80px_rgba(0,0,0,0.55)]"
        style={{ backgroundColor: OWNER_BG, borderColor: OWNER_BORDER }}
      >
        <div className="flex items-start justify-between gap-4 border-b p-5" style={{ borderColor: OWNER_BORDER }}>
          <div>
            <div className="text-lg font-semibold text-white">{title}</div>
            {subtitle && <div className="mt-1 text-sm text-white/60">{subtitle}</div>}
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            className="rounded-lg border px-3 py-1 text-xs text-white/70 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--owner-accent)]"
            style={{ borderColor: OWNER_BORDER }}
          >
            Close
          </button>
        </div>

        <div className="space-y-6 p-5">
          {fields && fields.length > 0 && (
            <div className="rounded-2xl border p-4" style={{ borderColor: OWNER_BORDER, backgroundColor: OWNER_PANEL }}>
              <div className="text-xs uppercase tracking-[0.2em] text-white/50">Details</div>
              <dl className="mt-3 space-y-2 text-sm text-white/80">
                {fields.map((field) => (
                  <div key={field.label} className="flex items-center justify-between gap-3">
                    <dt className="text-white/60">{field.label}</dt>
                    <dd className="text-white">{field.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          {table && (
            <div className="rounded-2xl border p-4" style={{ borderColor: OWNER_BORDER, backgroundColor: OWNER_PANEL }}>
              <div className="text-xs uppercase tracking-[0.2em] text-white/50">Recent activity</div>
              <div className="mt-3 overflow-x-auto">
                <table className="min-w-full text-left text-xs text-white/75">
                  <thead className="text-white/40">
                    <tr>
                      {table.columns.map((col) => (
                        <th key={col} className="pb-2 pr-3 font-normal">
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {table.rows.map((row, idx) => (
                      <tr key={`${idx}`}>
                        {row.map((cell, cellIdx) => (
                          <td key={`${idx}-${cellIdx}`} className="py-2 pr-3 text-white/70">
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </aside>
    </div>
  )
}
